import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import { Resource, Action } from '../../../Constants/socket';

/**
 * Custom hook to handle credential real-time socket events
 * @param {Array} credentials - Current credentials list
 * @param {Function} setCredentials - Function to update credentials
 * @returns {Object} Socket event data (highlighted rows, soft refresh key)
 */
const useCredentialSocketEvents = (credentials, setCredentials) => {
    const [softRefreshKey, setSoftRefreshKey] = useState(0);
    const [highlightedRowIds, setHighlightedRowIds] = useState(() => new Set());
    const highlightTimeoutsRef = useRef(new Map());

    // Memoize credential IDs for socket subscription
    const credentialIds = useMemo(() => {
        return credentials.map(credential => credential.id).filter(Boolean);
    }, [credentials]);

    // Subscribe to credential resource with specific IDs
    useSocketSubscription(Resource.CREDENTIAL, credentialIds.length > 0 ? credentialIds : null);

    const highlightRow = useCallback((id) => {
        if (!id) return;

        setHighlightedRowIds(prev => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });

        const existingTimeout = highlightTimeoutsRef.current.get(id);
        if (existingTimeout) clearTimeout(existingTimeout);

        const timeoutId = setTimeout(() => {
            setHighlightedRowIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            highlightTimeoutsRef.current.delete(id);
        }, 2500);

        highlightTimeoutsRef.current.set(id, timeoutId);
    }, []);

    useEffect(() => {
        return () => {
            // Cleanup highlight timers on unmount
            for (const timeoutId of highlightTimeoutsRef.current.values()) {
                clearTimeout(timeoutId);
            }
            highlightTimeoutsRef.current.clear();
        };
    }, []);

    const handleCredentialCreated = useCallback((data) => {
        // Avoid skeleton flash: do a silent/soft refresh and briefly highlight the new row
        if (data?.id) highlightRow(data.id);
        setSoftRefreshKey(prev => prev + 1);
    }, [highlightRow]);

    const handleCredentialUpdated = useCallback((data) => {
        // Update the specific credential in state without reloading
        setCredentials(prevCredentials =>
            prevCredentials.map(credential =>
                credential.id === data.id ? { ...credential, ...data } : credential
            )
        );
        if (data?.id) highlightRow(data.id);
    }, [setCredentials, highlightRow]);

    const handleCredentialDeleted = useCallback((data) => {
        // Remove the deleted credential from state
        setCredentials(prevCredentials =>
            prevCredentials.filter(credential => credential.id !== data.id)
        );
    }, [setCredentials]);

    // Register socket event listeners
    useSocketEvent(Resource.CREDENTIAL, Action.CREATE, handleCredentialCreated);
    useSocketEvent(Resource.CREDENTIAL, Action.UPDATE, handleCredentialUpdated);
    useSocketEvent(Resource.CREDENTIAL, Action.DELETE, handleCredentialDeleted);

    return {
        highlightedRowIds,
        softRefreshKey,
    };
};

export default useCredentialSocketEvents;
